import { useState, useEffect, useRef } from 'react';
import AssetManager from '../services/AssetManager.ts';
import { daysOfWeek } from '../config/constants';

/**
 * Custom hook to manage active days from AssetManager
 * Provides caching and error handling for day configuration
 */
export function useActiveDays() {
    const [activeDays, setActiveDays] = useState(daysOfWeek); // Default to all days
    const [activeDaysCapitalized, setActiveDaysCapitalized] = useState(
        daysOfWeek.map(day => day.charAt(0).toUpperCase() + day.slice(1))
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const assetManagerRef = useRef(AssetManager.getInstance());

    useEffect(() => {
        let isMounted = true;

        const fetchActiveDays = async () => {
            try {
                setLoading(true);
                setError(null);
                
                const assetManager = assetManagerRef.current;
                
                // Fetch both lowercase and capitalized versions
                const [lowercase, capitalized] = await Promise.all([
                    assetManager.getActiveDays(),
                    assetManager.getActiveDaysCapitalized()
                ]);
                
                if (isMounted) {
                    setActiveDays(lowercase);
                    setActiveDaysCapitalized(capitalized);
                }
            } catch (err) {
                console.error('[useActiveDays] Error loading active days:', err);
                if (isMounted) {
                    setError(err);
                    // Keep default values on error
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchActiveDays();

        return () => {
            isMounted = false;
        };
    }, []);

    /**
     * Check if a specific day is active
     * @param {string} day - The day name (case-insensitive)
     * @returns {boolean} True if the day is active
     */
    const isDayActive = (day) => {
        return activeDays.includes(day.toLowerCase());
    };

    /**
     * Get active days synchronously (uses cached data)
     * @returns {Array} Array of active day names (lowercase)
     */
    const getActiveDaysSync = () => {
        return activeDays;
    };

    /**
     * Get active days capitalized synchronously (uses cached data)
     * @returns {Array} Array of active day names (capitalized)
     */
    const getActiveDaysCapitalizedSync = () => {
        return activeDaysCapitalized;
    };

    /**
     * Refresh the active days from the server
     */
    const refreshActiveDays = async () => {
        try {
            const assetManager = assetManagerRef.current;
            
            // Clear the cache to force a fresh fetch
            assetManager.clearInstitutionSettingsCache();
            
            const [lowercase, capitalized] = await Promise.all([
                assetManager.getActiveDays(),
                assetManager.getActiveDaysCapitalized()
            ]);
            
            setActiveDays(lowercase);
            setActiveDaysCapitalized(capitalized);
        } catch (err) {
            console.error('[useActiveDays] Error refreshing active days:', err);
            setError(err);
        }
    };

    return {
        activeDays,
        activeDaysCapitalized,
        loading,
        error,
        isDayActive,
        getActiveDaysSync,
        getActiveDaysCapitalizedSync,
        refreshActiveDays
    };
}

export default useActiveDays; 